<template>
  <footer aria-label="Footer">
    <!-- Top footer -->
    <div
      v-if="!compact"
      class="bg-[color:var(--color-footer-top-bg)] py-8 text-sm leading-6 text-[color:var(--color-footer-top-text)] md:flex md:py-20"
    >
      <div
        class="container mx-auto grid w-full max-w-screen-xl grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5"
      >
        <!-- Logo column -->
        <div>
          <VcImage :src="$cfg.logo_inverted_image" :alt="$context.storeName" class="h-12" lazy />

          <div class="mt-2 flex flex-wrap">
            <template v-for="(social, index) in $cfg.socialItems" :key="index">
              <div class="mr-2.5 flex items-center">
                <!-- eslint-disable vuejs-accessibility/anchor-has-content -->
                <a
                  class="flex items-center rounded-full bg-[color:var(--color-body-bg)] text-[color:var(--color-primary)] hover:text-[color:var(--color-secondary-500)] p-1"
                  target="_blank"
                  :href="social.link"
                >
                  <VcIcon :name="social.type" :size="20" />
                </a>
                <!-- eslint-enable -->
              </div>
            </template>
          </div>
        </div>

        <!-- Column 1 -->
        <div v-if="footerListLeft">
          <div class="mb-3 font-amaranth text-lg font-bold">{{ $cfg.footerTitleLeft }}</div>
          <div class="flex flex-col space-y-1">
            <FooterLink v-for="item in footerListLeft" :key="item.id" :title="item.title" :to="item.route!" />
          </div>
        </div>

        <!-- Column 2 -->
        <div v-if="footerListCenterLeft">
          <div class="mb-3 font-amaranth text-lg font-bold">{{ $cfg.footerTitleCenterLeft }}</div>
          <div class="flex flex-col space-y-1">
            <FooterLink v-for="item in footerListCenterLeft" :key="item.id" :title="item.title" :to="item.route!" />
          </div>
        </div>

        <!-- Column 3 -->
        <div v-if="footerListCenterRight">
          <div class="mb-3 font-amaranth text-lg font-bold">{{ $cfg.footerTitleCenterRight }}</div>
          <div
            :class="[footerListCenterRight.length > 5 ? 'grid space-y-1 lg:grid-cols-2 lg:gap-x-2.5' : 'flex flex-col']"
          >
            <FooterLink v-for="item in footerListCenterRight" :key="item.id" :title="item.title" :to="item.route!" />
          </div>
        </div>

        <!-- Column 4 -->
        <div v-if="$cfg.footerTitleRight">
          <div class="mb-3 font-amaranth text-lg font-bold">{{ $cfg.footerTitleRight }}</div>
          <div v-if="activeBranch" class="flex flex-col space-y-1 leading-5">
            <span>{{ activeBranch.line1 }}</span>
            <span>{{ activeBranch.postalCode }} {{ activeBranch.city }}</span>
            <a class="hover:text-[color:var(--color-secondary-500)]" :href="`tel:${activeBranch.phone}`">{{
              activeBranch.phone
            }}</a>
            <a class="hover:text-[color:var(--color-secondary-500)]" :href="`mailto:${activeBranch.email}`">{{
              activeBranch.email
            }}</a>
          </div>

          <div v-else-if="$cfg.footerContactAddress">
            <div class="custom-footer-address" v-html-safe="$cfg.footerContactAddress"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom footer -->
    <div
      class="flex min-h-[3.2rem] border-t border-[#0E5A49] bg-[color:var(--color-footer-bottom-bg)] py-2 text-sm font-medium sm:flex-row md:px-12 print:flex-row print:bg-[color:var(--color-white)] print:px-0 print:text-[color:var(--color-black)]"
    >
      <div
        class="container mx-auto flex max-w-screen-xl flex-col space-y-2 pt-4 md:flex-row md:items-center md:justify-center md:space-x-6 md:space-y-0 md:px-0 md:pt-0"
      >
        <div v-if="footerListBottom" class="flex flex-col space-y-1 md:flex-row md:space-x-6 md:space-y-0">
          <FooterLink v-for="item in footerListBottom" :key="item.id" :title="item.title" :to="item.route!" />
        </div>

        <div class="mt-4 text-white md:mt-0" v-html-safe="$cfg.footerCopyright"></div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useNavigations } from "@/core/composables";
import { getDynamicPropertyValue } from "@/core/utilities";
import { useUser } from "@/shared/account";
import { useFulfillmentCenters } from "@/shared/fulfillmentCenters";
import FooterLink from "../_internal/footer-link.vue";
import type { IFulfillmentCenter } from "@/shared/fulfillmentCenters";

const props = defineProps({
  compact: Boolean,
});

const { fulfillmentCenters, loadFulfillmentCenters } = useFulfillmentCenters();
const { isAuthenticated, organization } = useUser();

const activeBranch = ref<IFulfillmentCenter | null>();
const SAP_PLANT_PROPERTY = "Sap Plant";

const sapPlant = computed<string>(
  () => getDynamicPropertyValue(organization.value?.dynamicProperties, SAP_PLANT_PROPERTY) ?? "",
);

const { footerListLeft, footerListCenterLeft, footerListCenterRight, footerListBottom, fetchFooterLinksInnovadis } =
  useNavigations();

onMounted(async () => {
  await loadFulfillmentCenters();

  if (isAuthenticated.value) {
    activeBranch.value = fulfillmentCenters.value.filter(
      (item: IFulfillmentCenter) => item.outerId === sapPlant.value,
    )[0];
  }
  if (!props.compact) {
    void fetchFooterLinksInnovadis();
  }
});
</script>

<style>
.custom-footer-address {
  position: relative;
  top: -4px;
}
.custom-footer-address p {
  line-height: 28px !important;
}
</style>
